/**
 * Sondage Ifop — overlay plein écran qui s'ouvre tous les 3 tours.
 *
 * C'est le rendez-vous attendu (cf. project_retention_doctrine, levier #3).
 * Le joueur sait que dans 3 mois, l'Ifop publiera sa cote — et il ajuste
 * ses décisions en conséquence. Affiche politique : grand chiffre, variation,
 * breakdown des trois cohortes les plus visibles.
 *
 * Trigger : `worldState.turn > 0 && turn % 3 === 0`. Une fois le sondage
 * affiché pour un tour donné, on retient `lastShownTurn` pour ne pas le
 * rouvrir au refresh ou à la navigation.
 */

import { useEffect, useState } from "react";
import type { Cohort, WorldState } from "../engine/types";
import { useGame } from "../store/useGame";

const POLL_PERIOD_MONTHS = 3;
const STORAGE_KEY = "mp.lastPollShownTurn";

const MEDIA_PARTNERS = [
  "Ifop · Le Figaro",
  "Elabe · BFMTV",
  "OpinionWay · Les Échos",
  "Harris · Challenges",
  "Odoxa · Le Parisien",
];

/** Note d'approbation entre 0 et 100. Lit directement le champ mesuré
 *  par le moteur (`polity.approval_rate`), qui combine satisfaction
 *  réelle + biais média + biais régime + bruit. Fallback sur le calcul
 *  historique pour les saves antérieures au 2026-05-12. */
function approvalFromState(state: WorldState): number {
  if (typeof state.polity.approval_rate === "number") {
    return Math.max(0, Math.min(100, Math.round(state.polity.approval_rate)));
  }
  const total = state.population.cohorts.reduce((a, c) => a + c.count, 0);
  if (total === 0) return 50;
  const sat =
    state.population.cohorts.reduce((a, c) => a + c.satisfaction * c.count, 0) /
    total;
  return Math.max(0, Math.min(100, Math.round(sat)));
}

/** Note de satisfaction par CSP, pondérée par la population de la CSP. */
function satisfactionByCsp(cohorts: Cohort[]): Map<string, number> {
  const acc = new Map<string, { sum: number; count: number }>();
  for (const c of cohorts) {
    const e = acc.get(c.csp) ?? { sum: 0, count: 0 };
    e.sum += c.satisfaction * c.count;
    e.count += c.count;
    acc.set(c.csp, e);
  }
  const out = new Map<string, number>();
  for (const [k, { sum, count }] of acc) {
    if (count > 0) out.set(k, sum / count);
  }
  return out;
}

const CSP_LABEL: Record<string, string> = {
  workers: "ouvriers et employés",
  executives: "cadres et professions libérales",
  self_employed: "indépendants et artisans",
  unemployed: "demandeurs d'emploi",
  retired: "retraités",
  students: "étudiants",
};

interface PollSnapshot {
  turn: number;
  approval: number;
  variation: number;
  media: string;
  breakdown: { csp: string; label: string; score: number; delta: number }[];
}

function pickMedia(turn: number): string {
  return MEDIA_PARTNERS[turn % MEDIA_PARTNERS.length];
}

function buildSnapshot(
  state: WorldState,
  history: WorldState | null,
): PollSnapshot {
  const approval = approvalFromState(state);
  const previous = history ? approvalFromState(history) : approval;
  const variation = approval - previous;

  const currentByCsp = satisfactionByCsp(state.population.cohorts);
  const previousByCsp = history
    ? satisfactionByCsp(history.population.cohorts)
    : new Map<string, number>();

  // On garde les 3 CSP avec le plus grand écart absolu — celles qui
  // racontent quelque chose. Si aucune n'a bougé, on prend les plus
  // peuplées.
  const sorted = [...currentByCsp.entries()]
    .map(([csp, score]) => {
      const prev = previousByCsp.get(csp) ?? score;
      return { csp, score, delta: score - prev };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 3);

  return {
    turn: state.turn,
    approval,
    variation,
    media: pickMedia(state.turn),
    breakdown: sorted.map((s) => ({
      csp: s.csp,
      label: CSP_LABEL[s.csp] ?? s.csp,
      score: Math.round(s.score),
      delta: Math.round(s.delta),
    })),
  };
}

export function PollOverlay() {
  const worldState = useGame((s) => s.worldState);
  const lastPollState = useGame((s) => s.lastPollState);
  const markPollSeen = useGame((s) => s.markPollSeen);
  const [shown, setShown] = useState(false);
  const [snapshot, setSnapshot] = useState<PollSnapshot | null>(null);

  useEffect(() => {
    if (!worldState) return;
    const turn = worldState.turn;
    const isPollTurn = turn > 0 && turn % POLL_PERIOD_MONTHS === 0;
    if (!isPollTurn) return;
    const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? "-1", 10);
    if (last === turn) return;
    // J2 — on compare au dernier sondage *réellement vu*, pas à null.
    setSnapshot(buildSnapshot(worldState, lastPollState));
    setShown(true);
  }, [worldState, lastPollState]);

  const close = () => {
    if (snapshot && worldState) {
      localStorage.setItem(STORAGE_KEY, String(snapshot.turn));
      // J2 — fige la baseline pour le prochain sondage (3 mois plus tard).
      markPollSeen(worldState);
    }
    setShown(false);
  };

  if (!shown || !snapshot) return null;

  const sign = snapshot.variation > 0 ? "+" : snapshot.variation < 0 ? "" : "±";
  const trend =
    snapshot.variation > 0 ? "up" : snapshot.variation < 0 ? "down" : "flat";

  return (
    <div className="poll-backdrop" onClick={close}>
      <div
        className="poll-card"
        role="dialog"
        aria-modal="true"
        aria-label="Sondage d'approbation"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="poll-head">
          <span className="poll-source">{snapshot.media}</span>
          <span className="poll-turn">SONDAGE · MOIS {snapshot.turn}</span>
        </header>

        <div className="poll-headline">
          <div className="poll-score" data-trend={trend}>
            {snapshot.approval}
            <span className="poll-pct">%</span>
          </div>
          <div className="poll-context">
            <p className="poll-label">d'opinions favorables</p>
            {snapshot.variation !== 0 && (
              <p className="poll-variation" data-trend={trend}>
                {sign}
                {snapshot.variation} pts depuis le dernier sondage
              </p>
            )}
          </div>
        </div>

        <ol className="poll-breakdown">
          {snapshot.breakdown.map((b) => (
            <li key={b.csp} className="poll-row">
              <span className="row-label">{b.label}</span>
              <span className="row-score">{b.score}</span>
              {b.delta !== 0 && (
                <span
                  className="row-delta"
                  data-trend={
                    b.delta > 0 ? "up" : b.delta < 0 ? "down" : "flat"
                  }
                >
                  {b.delta > 0 ? "+" : ""}
                  {b.delta}
                </span>
              )}
            </li>
          ))}
        </ol>

        <button type="button" className="poll-close" onClick={close}>
          REFERMER LE JOURNAL
        </button>
      </div>
    </div>
  );
}
